<template>
  <div :class="['content-block', 'news-block', `theme-${theme}`, { 'locale-zh-CN': isZhLocale }]">
    <div class="block-header">
      <div class="header-left">
        <h3 class="block-title">{{ title }}</h3>
        <span v-if="isZhLocale && subtitle" class="block-subtitle">{{ subtitle }}</span>
      </div>
      <div v-if="tabs && tabs.length > 0" class="header-line"></div>
      <div v-if="tabs && tabs.length > 0" class="header-tabs">
        <span
          v-for="tab in tabs"
          :key="tab.value"
          :class="['tab-item', { active: activeTab === tab.value }]"
          @click="handleTabClick(tab.value)"
        >
          {{ tab.label }}
        </span>
      </div>
      <div v-if="moreText" class="more-link" @click="handleMoreClick">
        {{ moreText }}
      </div>
    </div>
    <div class="block-content">
      <div 
        v-for="(item, index) in newsList" 
        :key="item.id || index" 
        class="news-item" 
        @click="handleNewsClick(item)"
      >
        <span class="news-icon">+</span>
        <span class="news-title">{{ item.title }}</span>
        <span class="news-date">{{ item.date }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getText } from '@/utils/i18nText'

export default {
  name: 'NewsBlock',
  props: {
    // 标题
    title: {
      type: String,
      required: true
    },
    // 副标题
    subtitle: {
      type: String,
      default: ''
    },
    // Tab选项（不传则不显示tab和line）
    tabs: {
      type: Array,
      default: () => []
    },
    // 当前激活的tab
    activeTab: {
      type: String,
      default: ''
    },
    // 更多文字
    moreText: {
      type: String,
      default: () => getText('newsBlock.moreText', '更多 >')
    },
    // 新闻列表
    newsList: {
      type: Array,
      default: () => []
    },
    // 样式主题：default(默认) | homepage(首页)
    theme: {
      type: String,
      default: 'default',
      validator: value => ['default', 'homepage'].includes(value)
    }
  },
  methods: {
    handleTabClick(value) {
      this.$emit('tab-change', value)
    },
    
    handleMoreClick() {
      this.$emit('more-click')
    },
    
    handleNewsClick(item) {
      this.$emit('news-click', item)
    }
  }
}
</script>

<style lang="scss" scoped>
.news-block {
  background: #FFFFFF;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.block-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E8E8E8;
}

.header-left {
  display: flex;
  flex-direction: column;
  
  .block-title {
        font-family: FZZhengHeiS-DB-GB;
        font-weight: 400;
        font-size: 20px;
        color: #444444;
        line-height: 26px;
        margin: 0;
      }
      
      .block-subtitle {
        font-family: Arial;
        font-weight: 400;
        font-size: 11px;
        color: #AA9A4C;
        line-height: 26px;
      }
}

.header-line {
  width: 1px;
  height: 24px;
  background: #DDDDDD;
  margin: 0 8px;
}

.header-tabs {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  
  .tab-item {
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #666666;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;
    padding-bottom: 4px;
    
    &:hover {
      color: #A0903C;
    }
    
    &.active {
      color: #A0903C;
      font-weight: 500;
      
      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: #A0903C;
      }
    }
  }
}

.more-link {
  font-family: Source Han Sans CN;
  font-size: 12px;
  color: #A0903C;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s;
  margin-left: auto;
  
  &:hover {
    color: #8C7B21;
    text-decoration: underline;
  }
}

.news-block.locale-zh-CN {
  .block-header .more-link {
    font-size: 16px;
  }

  .block-header .header-left .block-title {
    font-size: 25px;
  }

  .block-content .news-item {
    .news-title {
      font-size: 18px;
    }

    .news-date {
      font-size: 16px;
    }
  }
}

.block-content {
  display: flex;
  flex-direction: column;
}

.news-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #F8F8F8;
    
    .news-title {
      color: #A0903C;
    }
  }
  
  .news-icon {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #A0903C;
    font-weight: bold;
    flex-shrink: 0;
  }
  
  .news-title {
    flex: 1;
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #333333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: all 0.3s;
  }
  
  .news-date {
    font-family: Arial;
    font-size: 12px;
    color: #999999;
    flex-shrink: 0;
  }
}

// 首页主题特殊样式
.theme-homepage {
  background: transparent;
  border-radius: 0;
  padding: 0px 20px;
  box-shadow: none;
  
  .block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 0;
    border-bottom: none;
    gap: 16px;
    
    .header-left {
      display: flex;
      flex-direction: column;
      
      .block-title {
        font-family: FZZhengHeiS-DB-GB;
        font-weight: 400;
        font-size: 20px;
        color: #444444;
        line-height: 26px;
        margin: 0;
      }
      
      .block-subtitle {
        font-family: Arial;
        font-weight: 400;
        font-size: 11px;
        color: #AA9A4C;
        line-height: 26px;
      }
    }
    
    .header-line {
      width: 1px;
      height: 35px;
      background: #E2E2E2;
      margin: 0;
    }
    
    .header-tabs {
      display: flex;
      gap: 0;
      background: transparent;
      border-radius: 0;
      padding: 0;
      
      .tab-item {
        cursor: pointer;
        font-family: Source Han Sans CN;
        font-weight: 400;
        font-size: 16px;
        color: #555555;
        line-height: 22px;
        padding: 8px 24px;
        
        &::after {
          display: none;
        }
        
        &:first-child {
          margin-left: 0;
          border-radius: 4px 0 0 4px;
        }
        
        &:last-child {
          border-radius: 0 4px 4px 0;
        }
        
        &.active {
          background: #A0903C;
          color: #fff;
          border-color: #A0903C;
          z-index: 1;
        }
        
        &:hover:not(.active) {
          background: #e8e8e8;
          color: #555555;
        }
      }
    }
    
    .more-link {
      cursor: pointer;
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 12px;
      color: #A0903C;
      line-height: 22px;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  .block-content {
    gap: 0;
    
    .news-item {
      display: flex;
      align-items: center;
      padding: 10px 0;
      border-radius: 0;
      gap: 0;
      border-bottom: none;
      cursor: pointer;
      transition: all 0.3s;
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background: transparent;
        
        .news-title {
          color: #b8945f;
        }
      }
      
      .news-icon {
        width: auto;
        height: auto;
        color: #b8945f;
        font-size: 16px;
        margin-right: 8px;
        font-weight: normal;
      }
      
      .news-title {
        flex: 1;
        font-size: 14px;
        color: #666;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: color 0.3s;
      }
      
      .news-date {
        font-size: 12px;
        color: #b8945f;
        margin-left: 15px;
      }
    }
  }
}

.theme-homepage.locale-zh-CN {
  .block-header {
    .header-left .block-title {
      font-size: 25px;
    }

    .header-tabs .tab-item {
      font-size: 20px;
    }
  }

  .block-content .news-item .news-title {
    font-size: 18px;
  }
}
</style>
